import { useState, type FC, type ReactNode } from 'react';
import { type StreamForStreamDashboard } from 'Lessons';
import { ShowMoreCollapse } from './ShowMoreCollapse';
import { CreditsBox } from './CreditsBox';
import { RecommendedCoursesBox } from './RecommendedCoursesBox';
import { RelatedCoursesBox } from './RelatedCoursesBox';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { type CourseCard } from '../StreamDashboard.types';

/*
    The part of the sidebar cut short on mobile. It returns null with no boxes, because ShowMoreCollapse reads
    padding alone as hidden content.
*/
export const CourseInformationGroup: FC<{
    streamTitle: string;
    credits: StreamForStreamDashboard['credits'];
    recommendedCourses: CourseCard[];
    relatedCourses: CourseCard[];
    onOpenCourse: (streamId: string) => void;
    resources: ReactNode;
}> = ({ streamTitle, credits, recommendedCourses, relatedCourses, onOpenCourse, resources }) => {
    const isMobile = useIsMobileViewport();
    const [showAll, setShowAll] = useState(false);

    /*
        Each condition mirrors the box's own: RecommendedCoursesBox and RelatedCoursesBox return null
        without courses, and CreditsBox returns null for empty credits as well as null.
    */
    const hasContent = recommendedCourses.length > 0 || relatedCourses.length > 0 || !!resources || !!credits;

    if (!hasContent) return null;

    return (
        /* The fade ends in the page background (slate-grey-100-alt), not white. */
        <ShowMoreCollapse
            collapsed={isMobile && !showAll}
            onShowMore={() => setShowAll(true)}
            fadeClassName="bg-gradient-to-b from-transparent to-slate-grey-100-alt"
            collapsedMaxHeightClassName="max-h-0"
            controlClassName="mb-5"
        >
            {/* 18px here plus the last box's own 12px make 30px below the column on mobile, which is the
                gap above it, between the last chapter card and the first box. The padding sits inside the
                cut, so a collapsed column is unaffected and keeps the 20px below its control. */}
            <div className="max-md:pb-4.5">
                <RecommendedCoursesBox
                    courses={recommendedCourses}
                    streamTitle={streamTitle}
                    onSelectCourse={onOpenCourse}
                    className="mb-3 sm:mb-7.5"
                />
                <RelatedCoursesBox courses={relatedCourses} onSelectCourse={onOpenCourse} className="mb-3 sm:mb-7.5" />

                {resources}

                <CreditsBox credits={credits} className="mb-3 sm:mb-7.5" />
            </div>
        </ShowMoreCollapse>
    );
};
